import { useState, useEffect } from 'react';
import {
  DollarSign,
  FileText,
  TrendingUp,
  TrendingDown,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar,
  BarChart3,
  PieChart
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface BillingSummary {
  total_claims: number;
  total_charges: number;
  total_paid: number;
  paid_claims: number;
  denied_claims: number;
  avg_days_to_payment: number;
  payment_rate: number;
}

interface PayerPerformance {
  payer_name: string;
  total_claims: number;
  total_charges: number;
  total_paid: number;
  payment_rate: number;
  avg_days_to_payment: number;
  denial_rate: number;
}

interface DenialAnalytics {
  carc_code: string;
  carc_description: string;
  denial_count: number;
  total_denied_amount: number;
}

interface ARAgingBucket {
  bucket: string;
  claim_count: number;
  total_amount: number;
}

export default function BillingAnalyticsPage() {
  const { t } = useAppTranslation();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [payerPerformance, setPayerPerformance] = useState<PayerPerformance[]>([]);
  const [denialAnalytics, setDenialAnalytics] = useState<DenialAnalytics[]>([]);
  const [arAging, setArAging] = useState<ARAgingBucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId })
    };
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (dateRange.start_date) queryParams.append('start_date', dateRange.start_date);
      if (dateRange.end_date) queryParams.append('end_date', dateRange.end_date);

      // Fetch billing summary
      const summaryRes = await fetch(
        `${getWeaveinApiUrl('/api/billing/analytics/summary')}?${queryParams}`,
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
      }

      // Fetch payer performance
      const payerRes = await fetch(
        `${getWeaveinApiUrl('/api/billing/analytics/payer-performance')}?${queryParams}`,
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (payerRes.ok) {
        const data = await payerRes.json();
        // Backend returns array directly, not wrapped in { payers: [...] }
        setPayerPerformance(Array.isArray(data) ? data : (data.payers || []));
      }

      // Fetch denial analytics
      const denialRes = await fetch(
        `${getWeaveinApiUrl('/api/billing/analytics/denials/analytics')}?${queryParams}`,
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (denialRes.ok) {
        const data = await denialRes.json();
        // Backend returns array with different field names, map them
        const denialsList = Array.isArray(data) ? data : (data.denial_reasons || []);
        const mappedDenials = denialsList.map((d: any) => ({
          carc_code: d.denial_reason_code || d.carc_code,
          carc_description: d.denial_reason_description || d.carc_description,
          denial_count: d.denial_count,
          total_denied_amount: d.total_denied || d.total_denied_amount
        }));
        setDenialAnalytics(mappedDenials);
      }

      // Fetch AR aging
      const agingRes = await fetch(
        getWeaveinApiUrl('/api/billing/analytics/aging'),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (agingRes.ok) {
        const data = await agingRes.json();
        // Backend returns { aging_buckets: [...] }, map field names
        const agingList = data.aging_buckets || [];
        const mappedAging = agingList.map((a: any) => ({
          bucket: a.age_bucket || a.bucket,
          claim_count: a.claims_count || a.claim_count,
          total_amount: a.total_amount
        }));
        setArAging(mappedAging);
      }

    } catch (err: any) {
      setError(err.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading && !summary) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">{t('analytics.loading_analytics', 'Loading analytics...')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={fetchAnalytics}
            className="inline-flex items-center px-4 py-2 bg-v2accent text-white rounded-md hover:bg-v2accent/90 transition-colors"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('analytics.refresh', 'Refresh')}
          </button>
        </div>

        <div className="space-y-6">
        {/* Date Range Filter */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <label className="text-sm font-medium">{t('analytics.date_range', 'Date Range:')}</label>
            </div>
            <input
              type="date"
              value={dateRange.start_date}
              onChange={(e) => setDateRange(prev => ({ ...prev, start_date: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateRange.end_date}
              onChange={(e) => setDateRange(prev => ({ ...prev, end_date: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <button
              onClick={fetchAnalytics}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              {t('analytics.apply', 'Apply')}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Summary KPI Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('analytics.total_claims', 'Total Claims')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{summary.total_claims}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('analytics.total_charges', 'Total Charges')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(summary.total_charges)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('analytics.total_paid', 'Total Paid')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(summary.total_paid)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatPercent(summary.payment_rate)} collection rate
                  </p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('analytics.avg_days_to_payment', 'Avg Days to Payment')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {summary.avg_days_to_payment ? Math.round(summary.avg_days_to_payment) : 'N/A'}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('analytics.paid_claims', 'Paid Claims')}</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{summary.paid_claims}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">{t('analytics.denied_claims', 'Denied Claims')}</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{summary.denied_claims}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payer Performance Table */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">{t('analytics.payer_performance', 'Payer Performance')}</h2>
            <PieChart className="h-5 w-5 text-gray-500" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.payer', 'Payer')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.claims', 'Claims')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.charges', 'Charges')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.paid', 'Paid')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.payment_rate', 'Payment Rate')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.avg_days', 'Avg Days')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.denial_rate', 'Denial Rate')}</th>
                </tr>
              </thead>
              <tbody>
                {payerPerformance.map((payer, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{payer.payer_name}</td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">{payer.total_claims}</td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">{formatCurrency(payer.total_charges)}</td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">{formatCurrency(payer.total_paid)}</td>
                    <td className="py-3 px-4 text-sm text-right">
                      <span className={`font-medium ${payer.payment_rate >= 80 ? 'text-green-600' : 'text-yellow-600'}`}>
                        {formatPercent(payer.payment_rate)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">
                      {payer.avg_days_to_payment ? Math.round(payer.avg_days_to_payment) : 'N/A'}
                    </td>
                    <td className="py-3 px-4 text-sm text-right">
                      <span className={`font-medium ${payer.denial_rate > 10 ? 'text-red-600' : 'text-gray-600'}`}>
                        {formatPercent(payer.denial_rate)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {payerPerformance.length === 0 && (
              <div className="text-center py-8 text-gray-500">{t('analytics.no_payer_performance_data_available', 'No payer performance data available')}</div>
            )}
          </div>
        </div>

        {/* Denial Analytics */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">{t('analytics.top_denial_reasons', 'Top Denial Reasons')}</h2>
            <AlertCircle className="h-5 w-5 text-red-500" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.carc_code', 'CARC Code')}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.description', 'Description')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.count', 'Count')}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">{t('analytics.denied_amount', 'Denied Amount')}</th>
                </tr>
              </thead>
              <tbody>
                {denialAnalytics.slice(0, 10).map((denial, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">{denial.carc_code}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{denial.carc_description}</td>
                    <td className="py-3 px-4 text-sm text-right text-gray-600">{denial.denial_count}</td>
                    <td className="py-3 px-4 text-sm text-right text-red-600 font-medium">
                      {formatCurrency(denial.total_denied_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {denialAnalytics.length === 0 && (
              <div className="text-center py-8 text-gray-500">{t('analytics.no_denial_data_available', 'No denial data available')}</div>
            )}
          </div>
        </div>

        {/* AR Aging */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">{t('analytics.accounts_receivable_aging', 'Accounts Receivable Aging')}</h2>
            <Clock className="h-5 w-5 text-gray-500" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {arAging.map((bucket, index) => (
              <div key={index} className="bg-white rounded-lg border-2 p-4">
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-500">{bucket.bucket}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">{bucket.claim_count}</p>
                  <p className="text-sm text-gray-600 mt-1">claims</p>
                  <p className="text-lg font-semibold text-blue-600 mt-2">{formatCurrency(bucket.total_amount)}</p>
                </div>
              </div>
            ))}
          </div>
          {arAging.length === 0 && (
            <div className="text-center py-8 text-gray-500">{t('analytics.no_aging_data_available', 'No aging data available')}</div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
